<template>
    <v-container>
        <v-row v-for="announcement in meet.announcements" :key="announcement.id">
            <v-col>
                <v-alert border="top" colored-border type="info" elevation="2">
                    <strong>{{ announcement.title }}</strong>
                    <p v-html="announcement.description"></p>
                </v-alert>
            </v-col>
        </v-row>

        <v-row>
            <v-col>
                <v-card>
                    <v-card-text>
                        <meet-header
                            :meet-id="meetId"
                            :advertisements="advertisements"
                            :live-streams="liveStreams"
                            :is-selection-meet="isSelectionMeet"
                            no-title
                        />
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-tabs v-model="selectedTab" center-active show-arrows @change="onTabChange">
                    <v-tab v-for="tab in tabs" :key="tab.href" :href="tab.href" @click="tab.click">
                        {{ tab.label }}
                    </v-tab>
                </v-tabs>

                <v-tabs-items v-model="selectedTab" touchless>
                    <v-tab-item value="information">
                        <v-card>
                            <v-card-text>
                                <p v-html="meet.information"></p>
                            </v-card-text>
                        </v-card>
                    </v-tab-item>

                    <v-tab-item value="start-lists">
                        <v-skeleton-loader
                            v-if="loadingStartLists"
                            type="table-heading, list-item-three-line"
                            boilerplate
                        />
                        <v-card v-else-if="hasNoStartList" flat>
                            <v-card-text>No start list yet.</v-card-text>
                        </v-card>
                        <session-start-list v-else :meet-id="meetId" />
                    </v-tab-item>

                    <v-tab-item value="results">
                        <v-card v-if="hasNoResults" flat>
                            <v-card-text> No results yet. </v-card-text>
                        </v-card>
                        <v-row v-else>
                            <v-col v-if="hasTrampolineResults">
                                <levels-list
                                    :meet-id="meetId"
                                    :result-category-ids="resultCategoryIds"
                                    :live-result-category-ids="liveResultCategories"
                                    event="trampoline"
                                />
                            </v-col>

                            <v-col v-if="hasDoubleMiniResults">
                                <levels-list
                                    :meet-id="meetId"
                                    :result-category-ids="resultCategoryIds"
                                    :live-result-category-ids="liveResultCategories"
                                    event="double-mini"
                                />
                            </v-col>
                            <v-col v-if="hasTumblingResults">
                                <levels-list
                                    :meet-id="meetId"
                                    :result-category-ids="resultCategoryIds"
                                    :live-result-category-ids="liveResultCategories"
                                    event="tumbling"
                                />
                            </v-col>

                            <v-col v-if="hasSynchroResults">
                                <levels-list
                                    :meet-id="meetId"
                                    :result-category-ids="resultCategoryIds"
                                    :live-result-category-ids="liveResultCategories"
                                    event="synchro"
                                />
                            </v-col>

                            <v-col v-if="meet.all_around">
                                <levels-list
                                    :meet-id="meetId"
                                    :result-category-ids="resultCategoryIds"
                                    :live-result-category-ids="liveResultCategories"
                                    event="all-around"
                                />
                            </v-col>
                        </v-row>
                    </v-tab-item>

                    <v-tab-item value="club-results">
                        <v-card v-if="hasNoResults" flat>
                            <v-card-text> No results yet. </v-card-text>
                        </v-card>

                        <club-meet-results v-else :meet-id="meetId" />
                    </v-tab-item>

                    <v-tab-item value="team-results">
                        <v-skeleton-loader
                            v-if="loadingTeamResults"
                            type="table-heading, list-item-three-line"
                            boilerplate
                        />
                        <team-results
                            v-else-if="teamResults"
                            :team-results="teamResults"
                            @refresh="loadTeamResults(true)"
                        />
                    </v-tab-item>

                    <v-tab-item value="medal-count">
                        <v-skeleton-loader
                            v-if="loadingMedalCount"
                            type="table-heading, list-item-three-line"
                            boilerplate
                        />
                        <medal-count v-else-if="medalCount.length > 0" :club-medal-counts="medalCount" />
                    </v-tab-item>

                    <v-tab-item v-if="meet.has_extra_awards" value="extra-awards">
                        <v-alert border="left" class="mx-8 mt-2" colored-border type="info">
                            <p>
                                These are considered live rankings until the conclusion of the meet and may change while
                                the meet is running. Please wait for the final rankings to be confirmed.
                            </p>

                            <p class="font-weight-bold">These rankings are updated every 10 minutes.</p>
                        </v-alert>
                        <extra-awards :rankings="extraAwards" />
                    </v-tab-item>

                    <v-tab-item value="qualifiers">
                        <v-alert
                            v-if="$moment.utc().isSameOrBefore(meet.timestamp('end_date').add('day'))"
                            border="left"
                            class="mx-8 mt-2"
                            colored-border
                            type="info"
                        >
                            This report is updated every 10 minutes.
                        </v-alert>

                        <v-alert
                            v-if="!meet.elite_mobility"
                            border="left"
                            class="mx-8 mt-2"
                            colored-border
                            type="warning"
                        >
                            This meet is not an elite mobility meet, but still shows if the score was hit.
                        </v-alert>

                        <qualifiers :id="meetId" identifier="meet" :meet="meet" class="mt-4" />
                    </v-tab-item>

                    <v-tab-item value="statistics">
                        <meet-statistics :meet="meet" />
                    </v-tab-item>

                    <v-tab-item value="club-schedule">
                        <club-schedule :meet="meet" keep-alive />
                    </v-tab-item>
                </v-tabs-items>

                <v-alert v-if="hasAnyOpenElite" class="mt-4" dense type="info">
                    The new Open division temporarily appears as Open Elite.
                </v-alert>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import flatten from 'lodash/flatten.js';
import numeral from 'numeral';
import Meet from '@/Models/Meet.js';
import LevelsList from '@/Components/LevelsList.vue';
import MeetHeader from '@/Components/MeetHeader.vue';
import SessionStartList from '@/Components/SessionStartList.vue';
import ResultCategory from '@/Models/ResultCategory.js';
import PageMixin from '@/Pages/Page.mixin.js';
import Qualifiers from '@/Components/Qualifiers.vue';
import QueryBuilder from '@/query-builder.js';
import Result from '@/Models/Result.js';
import TeamResults from '@/Components/TeamResults.vue';
import Club from '@/Models/Club.js';
import Athlete from '@/Models/Athlete.js';
import MeetStatistics from '@/Components/MeetStatistics/MeetStatistics.vue';
import User from '@/Models/User.js';
import ClubSchedule from '@/Components/Club/Schedule/ClubSchedule.vue';
import ExtraAwards from '@/Pages/ExtraAwards.vue';
import ClubMeetResults from '@/Components/ClubMeetResults.vue';
import MedalCount from '@/Components/MedalCount.vue';

export default {
    name: 'Meet',

    components: {
        MedalCount,
        ExtraAwards,
        ClubSchedule,
        MeetStatistics,
        TeamResults,
        SessionStartList,
        MeetHeader,
        LevelsList,
        Qualifiers,
        ClubMeetResults,
    },

    mixins: [PageMixin],

    props: {
        meetId: {
            type: Number,
            required: true,
        },

        resultCategories: {
            type: Array,
            required: true,
        },

        // club_result_categories: {
        //     type: Array,
        //     required: true
        // },

        advertisements: {
            type: Array,
            required: false,
            default: () => [],
        },

        liveStreams: {
            type: Array,
            required: false,
            default: () => [],
        },

        isSelectionMeet: {
            type: Boolean,
            required: false,
            default: false,
        },

        medalCount: {
            type: Array,
            required: false,
            default: () => [],
        },

        extraAwards: {
            type: Object,
            required: false,
            default: () => ({}),
        },
    },

    data: () => ({
        selectedTab: null,
        loadingStartLists: false,
        loadingTeamResults: false,
        loadingMedalCount: false,
        teamResults: null,
        newResultCategories: [],
        liveResultCategories: [],
        timers: {},
        hasStartList: false,
    }),

    computed: {
        Result() {
            return Result;
        },

        user() {
            return User.find(this.$page.props.auth.user?.id);
        },

        meet() {
            return Meet.query().with('announcements').find(this.meetId);
        },

        tabs() {
            return [
                {
                    label: 'Info',
                    href: '#information',
                    condition: this.meet.information,
                    click: () => {},
                },
                {
                    label: 'Start Lists',
                    href: '#start-lists',
                    condition: true,
                    click: this.loadStartLists,
                },
                {
                    label: 'Results',
                    href: '#results',
                    condition: true,
                    click: () => {},
                },
                {
                    label: 'Club Schedule',
                    href: '#club-schedule',
                    condition: true,
                    click: () => {},
                },
                {
                    label: 'Club Results',
                    href: '#club-results',
                    condition: true,
                    click: () => {},
                },
                {
                    label: 'Team Results',
                    href: '#team-results',
                    condition: this.meet.team_definition_settings_count > 0,
                    click: () => {},
                },
                {
                    label: 'Medal Count',
                    href: '#medal-count',
                    condition: true,
                    click: () => {},
                },
                {
                    label: 'Extra Awards',
                    href: '#extra-awards',
                    condition: this.meet.has_extra_awards,
                    click: () => {},
                },
                {
                    label: 'Qualifiers',
                    href: '#qualifiers',
                    condition: this.meet.qualification_meet,
                    click: () => {},
                },
                {
                    label: 'Statistics',
                    href: '#statistics',
                    condition: this.user?.is_admin,
                    click: () => {},
                },
            ].filter((tab) => tab.condition);
        },

        allResultCategories() {
            return [...this.resultCategories, ...this.newResultCategories];
        },

        hasNoStartList() {
            return !this.hasStartList && !this.loadingStartLists;
        },

        hasNoResults() {
            return (
                !this.hasTrampolineResults &&
                !this.hasDoubleMiniResults &&
                !this.hasTumblingResults &&
                !this.hasSynchroResults
            );
        },

        resultCategoryIds() {
            return this.allResultCategories.map((resultCategory) => resultCategory.id);
        },

        hasTrampolineResults() {
            return this.allResultCategories.filter((resultCategory) => resultCategory.event === 'TI').length > 0;
        },

        hasDoubleMiniResults() {
            return this.allResultCategories.filter((resultCategory) => resultCategory.event === 'DM').length > 0;
        },

        hasTumblingResults() {
            return this.allResultCategories.filter((resultCategory) => resultCategory.event === 'TU').length > 0;
        },

        hasSynchroResults() {
            return this.allResultCategories.filter((resultCategory) => resultCategory.event === 'TS').length > 0;
        },

        hasAnyOpenElite() {
            return this.allResultCategories.some((resultCategory) => resultCategory.level === 'OE');
        },
    },

    watch: {
        async selectedTab(tab) {
            if (tab === 'team-results') {
                await this.loadTeamResults();
            }

            if (tab === 'start-lists') {
                this.$echo.channel(`meets-${this.meetId}`).listen('MeetStartListUpdated', this.loadStartLists);
            } else {
                this.$echo.leave(`meets-${this.meetId}`);
            }
        },
    },

    async created() {
        this.handleInitialTabSelection();

        this.resultCategories.forEach((resultCategory) => {
            if (this.$moment.utc(resultCategory.updated_at).isAfter(this.$moment.utc().subtract(10, 'minutes'))) {
                this.liveResultCategories.push(resultCategory.id);
            }
        });

        this.$echo.channel(`meets-${this.meetId}`).listen('ResultCategoryUpdated', this.setLiveStatus);
    },

    beforeDestroy() {
        this.$echo.leave(`meets-${this.meetId}`);
    },

    methods: {
        numeral,

        async setLiveStatus(event) {
            if (event.meetId !== this.meetId) {
                return;
            }

            const inserted = await ResultCategory.insert({ data: event.resultCategory });

            const resultCategory = inserted[ResultCategory.entity][0];

            this.newResultCategories.push(resultCategory);

            this.liveResultCategories.push(resultCategory.id);

            if (this.timers[resultCategory.id]) {
                clearInterval(this.timers[resultCategory.id]);
            }

            // Remove after 5 minutes
            this.timers[resultCategory.id] = setTimeout(
                () => {
                    this.liveResultCategories = this.liveResultCategories.filter((id) => id !== resultCategory.id);
                },
                1000 * 60 * 5,
            );
        },

        async loadStartLists() {
            if (this.hasStartList) {
                return;
            }

            this.loadingStartLists = true;

            const { response } = await Meet.api().get(`/api/meets/${this.meetId}`);

            this.hasStartList = _.get(response, 'data.data.sessions', []).length > 0;

            this.loadingStartLists = false;
        },

        async loadTeamResults(silently = false) {
            try {
                if (!silently) {
                    this.loadingTeamResults = true;
                }

                const query = new QueryBuilder(`meets/${this.meetId}/team-results`);

                const { data } = await axios.get(query.toUrl());

                this.teamResults = data.data;

                this.loadAthletesAndClubs();
            } finally {
                if (!silently) {
                    this.loadingTeamResults = false;
                }
            }
        },

        async loadAthletesAndClubs() {
            const flattenedTeamResults = flatten(this.teamResults.map((teamResult) => teamResult.results));

            await Club.insert({ data: flattenedTeamResults.map((result) => result.club) });

            flattenedTeamResults.forEach((result) => {
                if (result.trampolineResults) {
                    result.trampolineScores.forEach(({ athlete }) => {
                        if (athlete) {
                            Athlete.insert({ data: athlete });
                        }
                    });
                }

                if (result.doubleMiniScores) {
                    result.doubleMiniScores.forEach(({ athlete }) => {
                        if (athlete) {
                            Athlete.insert({ data: athlete });
                        }
                    });
                }

                if (result.tumblingScores) {
                    result.tumblingScores.forEach(({ athlete }) => {
                        if (athlete) {
                            Athlete.insert({ data: athlete });
                        }
                    });
                }
            });
        },

        async loadMedalCount() {
            await this.$inertia.reload({
                only: ['medalCount'],
                onStart: () => (this.loadingMedalCount = true),
                onFinish: () => (this.loadingMedalCount = false),
            });
        },

        handleInitialTabSelection() {
            const { hash } = window.location;

            if (hash) {
                this.selectedTab = hash.slice(1);

                if (this.selectedTab === 'start-lists') {
                    this.loadStartLists();
                } else if (this.selectedTab === 'team-results') {
                    this.loadTeamResults();
                } else if (this.selectedTab === 'medal-count') {
                    this.loadMedalCount();
                }
            } else if (this.$moment().isBefore(this.meet.timestamp('start_date'))) {
                this.selectedTab = 'start-lists';

                this.loadStartLists();
            } else {
                this.selectedTab = 'results';
            }
        },

        onTabChange(newTab) {
            this.selectedTab = newTab;
            const newUrl = `${window.location.pathname}#${newTab}`;
            window.history.pushState({ path: newUrl }, '', newUrl);

            if (newTab === 'medal-count') {
                this.loadMedalCount();
            }
        },
    },
};
</script>

<style lang="scss">
.v-slide-group__prev {
    display: none !important;
}
</style>
